import { useQuery } from "react-query";
import type { UseQueryOptions } from "react-query";
import { RequestHeaders } from "@certa/network/src/headers";
import { APIFetchV2 } from "@certa/network";
import { getWorkflowUniqKey } from "../../utils/workflow.utils";
import type { UseQueryReturnType } from "@certa/common/types/reactQueryGeneric.types";
import type { WorkflowItemDetailsResponse } from "../../types/workflow.types";
import { taskDetailsModelCreator } from "../../models/workflow.model";

export const getWorkflowDetails = async (
  workflowId: number | null | undefined
) => {
  if (!workflowId) throw new Error("Workflow ID is required");

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const detailsURL = `workflows/?id=${workflowId}`;
  const response = await APIFetchV2<WorkflowItemDetailsResponse>(
    detailsURL,
    requestOptions
  );

  // Check for empty results - this is the "Process not found" case
  // Replaces the old Redux behavior: if (!workflowDetails.results || workflowDetails.results.length === 0)
  if (!response.results || response.results.length === 0) {
    const error = new Error("Process not found") as Error & { status: number };
    error.status = 404;
    throw error;
  }

  return taskDetailsModelCreator(response);
};

export const getWorkflowDetailsQuery = (
  workflowId: number | null | undefined
): UseQueryOptions<UseQueryReturnType<typeof getWorkflowDetails>> => {
  return {
    queryKey: getWorkflowUniqKey(workflowId),
    queryFn: () => getWorkflowDetails(workflowId),
    enabled: workflowId !== null && workflowId !== undefined,
    retry: 0,
    staleTime: 2000
  };
};

export const useGetWorkflowDetails = (
  workflowId: number | null | undefined,
  config?: UseQueryOptions<UseQueryReturnType<typeof getWorkflowDetails>>
) =>
  useQuery<UseQueryReturnType<typeof getWorkflowDetails>>({
    ...getWorkflowDetailsQuery(workflowId),
    ...config
  });
